import * as Sentry from '@sentry/react';
import * as React from 'react';

import { Alert, Button, Skeleton, StepControls } from '@gitbook/spine-react';

import {
    GitSyncProgressModalStepper,
    IntegrationStepWrapper,
    useGitSyncCurrentStatus,
} from '../../../../Components';
import type { GitSyncInstallationStepProps } from '../types';

type GitSyncInstallationLastStepProps = GitSyncInstallationStepProps & { onNext: () => void };

/**
 * Sync progress step inside a GitSync installation flow.
 */
export function SyncProgressStep(props: GitSyncInstallationLastStepProps) {
    return (
        <IntegrationStepWrapper
            title="Sync progress"
            description="The syncing process is in progress. This may take a few minutes. You can safely close this dialog, it won't impact the sync."
        >
            <Sentry.ErrorBoundary
                fallback={
                    <Alert
                        kind="danger"
                        title="Error while updating GitSync status"
                        details="Something went wrong while loading the Git Sync status for this space. Please try reloading the app."
                    />
                }
            >
                <React.Suspense
                    fallback={
                        <div className="flex flex-col gap-6">
                            <Skeleton />
                            <Skeleton />
                            <Skeleton />
                        </div>
                    }
                >
                    <SyncProgressStepStatus {...props} />
                </React.Suspense>
            </Sentry.ErrorBoundary>
        </IntegrationStepWrapper>
    );
}

function SyncProgressStepStatus(props: GitSyncInstallationLastStepProps) {
    const { space } = props;

    const gitSyncStatus = useGitSyncCurrentStatus(space);

    return (
        <div className="mx-auto w-3/4">
            <GitSyncProgressModalStepper space={space} gitSyncStatus={gitSyncStatus} />
            {gitSyncStatus?.state === 'success' ? (
                <StepControls>
                    <div className="flex w-full justify-end gap-4">
                        {space.gitSync?.url ? (
                            <Button
                                label="View repository"
                                kind="secondary"
                                href={space.gitSync.url}
                                target="_blank"
                                rel="noopener noreferrer"
                            />
                        ) : null}
                        <Button label="View space" onPress={props.onNext} />
                    </div>
                </StepControls>
            ) : null}
        </div>
    );
}
